/**
 * Left rail (L.2.2, §3B.1) — a fixed 360px rail tabbed between the Anomaly Queue
 * (default-active, the L.0 first-glance surface), the Layer panel, the News & Social
 * wire (#65/#183 wave 3b — never default-active, ambient by doctrine), and the
 * Coordination group (#387/#378/#380 — Incidents · Assist · Requests worklists under
 * one tab), collapsible to a 44px count-badge rail. Never overlays the map centre.
 *
 * PEOC rail foundation (2026-06-21, founder Phase-0 ruling): the three coordination
 * worklists are grouped under one "Coordination" tab (design-council PART IX — quiet
 * COP, burden-of-proof on adding) so the primary row stays at four. The Shift Log
 * (#148, session-context) relocated off the rail to the CommandBar Settings ▾ menu
 * (#381).
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { logAction } from './lib/operatorActions';
import { useNewsFocus } from './newsFocus';
import { ALERTS_UNKNOWN_BADGE, ALERTS_UNKNOWN_TITLE } from './apiHealthVocabulary';

export interface LeftRailProps {
  /** The Anomaly Queue panel (default-active tab). */
  queue: ReactNode;
  /** The Layer panel (embedded LayerToggle). */
  layers: ReactNode;
  /** The News & Social wire feed (#65/#183 wave 3b tab). */
  news: ReactNode;
  /** The Coordination group (#387/#378/#380 — Incidents · Assist · Requests). */
  coordination: ReactNode;
  /** Active anomaly count, shown on the collapsed rail badge + the Alerts tab badge.
   *  #325 — `null` = UNKNOWN (API unreachable): the badge renders "—", never a false
   *  "0" all-clear. Distinguishing UNKNOWN from ZERO is the honest-degradation fix. */
  anomalyCount: number | null;
  /** Aggregate open coordination count (incidents + assist + drone requests),
   *  badged on the Coordination tab. */
  coordinationCount: number;
}

type Tab = 'queue' | 'layers' | 'news_social' | 'coordination';

export function LeftRail({
  queue,
  layers,
  news,
  coordination,
  anomalyCount,
  coordinationCount,
}: LeftRailProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('queue');
  const [collapsed, setCollapsed] = useState(false);

  // The #183 corroboration chip opens this rail's News tab (the feed itself
  // applies the bbox via its own bridge listener).
  useNewsFocus(
    useCallback(() => {
      setCollapsed(false);
      setTab('news_social');
    }, []),
  );

  // Responsive region law (council Part IV): below 1280px the rail auto-collapses
  // to the 44px count strip so the map keeps its centre. The listener fires only
  // when the breakpoint is crossed, so a manual expand inside one range sticks.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1279px)');
    const apply = (): void => setCollapsed(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  if (collapsed) {
    return (
      <aside
        data-testid="left-rail-collapsed"
        className="w-[var(--rail-w-collapsed)] shrink-0 border-r border-slate-700 bg-slate-900/95 text-slate-100 flex flex-col items-center py-2 gap-2"
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title="Expand"
          className="text-slate-400 hover:text-slate-100 text-lg leading-none"
        >
          »
        </button>
        <button
          type="button"
          onClick={() => {
            setCollapsed(false);
            setTab('queue');
          }}
          title={anomalyCount === null ? ALERTS_UNKNOWN_TITLE : `Urgent Alerts · ${anomalyCount}`}
          className="mt-1 flex flex-col items-center gap-1"
        >
          <span className="text-base">🔔</span>
          <span className="min-w-[20px] text-center text-[11px] font-semibold rounded-full bg-rose-600 px-1 py-0.5">
            {anomalyCount === null ? ALERTS_UNKNOWN_BADGE : anomalyCount}
          </span>
        </button>
        {/* #424 — keep the Anomaly Queue mounted (hidden) while collapsed so it
            keeps polling and emitting its urgent count: the collapsed badge above
            must stay live, exactly as the old map-layer-derived count did. The
            panel attaches no global listeners, so a display:none mount is inert
            beyond its own poll. */}
        <div className="hidden">{queue}</div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="left-rail"
      className="w-[var(--rail-w)] shrink-0 border-r border-slate-700 bg-slate-900/95 text-slate-100 flex flex-col overflow-hidden"
    >
      <div className="flex items-center border-b border-slate-700">
        <TabButton active={tab === 'queue'} onClick={() => setTab('queue')}>
          Alerts
          {(anomalyCount === null || anomalyCount > 0) && (
            <span
              className="ml-1.5 text-[10px] rounded-full bg-rose-600 px-1.5 py-0.5"
              title={anomalyCount === null ? ALERTS_UNKNOWN_TITLE : undefined}
            >
              {anomalyCount === null ? ALERTS_UNKNOWN_BADGE : anomalyCount}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'coordination'} onClick={() => setTab('coordination')}>
          Coordination
          {coordinationCount > 0 && (
            <span
              data-testid="coordination-tab-badge"
              className="ml-1.5 text-[10px] rounded-full bg-sky-600 px-1.5 py-0.5"
            >
              {coordinationCount}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'news_social'} onClick={() => setTab('news_social')}>
          News
        </TabButton>
        <TabButton active={tab === 'layers'} onClick={() => setTab('layers')}>
          Layers
        </TabButton>
        <button
          type="button"
          onClick={() => {
            setCollapsed(true);
            logAction('close_panel', { panel: 'left_rail' });
          }}
          title="Collapse"
          className="ml-auto px-2 text-slate-400 hover:text-slate-100"
        >
          «
        </button>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className={tab === 'queue' ? 'h-full' : 'hidden'}>{queue}</div>
        <div className={tab === 'coordination' ? 'h-full' : 'hidden'}>{coordination}</div>
        <div className={tab === 'news_social' ? 'h-full' : 'hidden'}>{news}</div>
        <div className={tab === 'layers' ? 'h-full overflow-y-auto' : 'hidden'}>{layers}</div>
      </div>
    </aside>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center px-3 py-2 text-xs font-medium ${
        active ? 'text-slate-100 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}
